import { Loader2 } from 'lucide-react';
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from 'src/components/ui/sheet';
import { formatDisplayDateTime } from 'src/helpers/date.helpers';
import type { CreativeTemplateExample, CreativeTemplateExampleWritePayload } from '../types/creative-template-example.types';
import { CreativeTemplateExampleForm } from './creative-template-example-form';
import type { EntitySheetMode } from './vertical-profile-sheet';

interface CreativeTemplateExampleSheetProps {
  open: boolean;
  mode: EntitySheetMode;
  example: CreativeTemplateExample | null;
  isSaving?: boolean;
  submitError?: string | null;
  profileOptions?: string[];
  onOpenChange: (open: boolean) => void;
  onSubmit: (values: CreativeTemplateExampleWritePayload) => void | Promise<void>;
}

export function CreativeTemplateExampleSheet({
  open,
  mode,
  example,
  isSaving = false,
  submitError = null,
  profileOptions = [],
  onOpenChange,
  onSubmit,
}: CreativeTemplateExampleSheetProps) {
  const isCreate = mode === 'create';

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent side="right" className="flex w-full flex-col gap-0 overflow-y-auto bg-white p-0 sm:max-w-5xl">
        <div className="border-b border-gray-400 px-6 py-5">
          <SheetHeader className="space-y-1 text-left">
            <SheetTitle className="text-xl font-medium text-black">
              {isCreate ? 'New template example' : example?.example_id ?? 'Template example'}
            </SheetTitle>
            <SheetDescription className="text-sm text-gray-500">
              Edit content overrides and brand bindings with a live preview against the canonical demo brand.
            </SheetDescription>
          </SheetHeader>
          {!isCreate && example ? (
            <dl className="mt-4 grid grid-cols-2 gap-3 text-sm md:grid-cols-4">
              <div>
                <dt className="text-xs font-medium text-gray-500">Example ID</dt>
                <dd className="mt-0.5 text-black">{example.example_id}</dd>
              </div>
              <div>
                <dt className="text-xs font-medium text-gray-500">Profile</dt>
                <dd className="mt-0.5 text-black">{example.profile_id}</dd>
              </div>
              <div>
                <dt className="text-xs font-medium text-gray-500">Version</dt>
                <dd className="mt-0.5 text-black">{example.version ?? '—'}</dd>
              </div>
              <div>
                <dt className="text-xs font-medium text-gray-500">Updated</dt>
                <dd className="mt-0.5 text-black">{formatDisplayDateTime(example.updated_at)}</dd>
              </div>
            </dl>
          ) : null}
        </div>

        <div className="flex-1 px-6 py-5">
          <CreativeTemplateExampleForm
            key={example?.id ?? 'create'}
            initialExample={isCreate ? null : example}
            isCreate={isCreate}
            isSaving={isSaving}
            submitError={submitError}
            profileOptions={profileOptions}
            onSubmit={onSubmit}
            onCancel={() => onOpenChange(false)}
          />
        </div>

        {isSaving ? (
          <div className="sticky bottom-0 flex items-center gap-2 border-t border-gray-400 bg-black px-6 py-3 text-sm text-white" role="status">
            <Loader2 className="size-4 animate-spin" />
            Saving…
          </div>
        ) : null}
      </SheetContent>
    </Sheet>
  );
}
